import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronRight, Power, RotateCcw, XCircle } from "lucide-react";
import { useState } from "react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type {
  FeatureEntitlementEntry,
  FeatureProviderRow,
  FeatureScopeKind,
} from "@tilt/admin-sdk";

import { Toggle } from "./Toggle";

/**
 * ApplicationsTab - feature entitlement tree for a scope (partner / org /
 * location). Groups features by their depth-1 root (e.g. "pos") and renders
 * an expandable card per group. Every toggle calls the V0060 feature-
 * entitlements endpoint.
 */
export interface ApplicationsTabProps {
  readonly scope: FeatureScopeKind;
  readonly id: string;
  readonly readOnly?: boolean;
}

export function ApplicationsTab({ scope, id, readOnly }: ApplicationsTabProps): JSX.Element {
  const featuresQuery = useQuery({
    queryKey: ["feature-entitlements", scope, id],
    queryFn: () => apiClient.listScopeFeatureEntitlements(scope, id),
    enabled: !!id,
    retry: false,
    staleTime: 30_000,
  });

  const providersQuery = useQuery({
    queryKey: ["feature-providers", scope, id],
    queryFn: () => apiClient.listScopeFeatureProviders(scope, id),
    enabled: !!id,
    retry: false,
    staleTime: 30_000,
  });

  if (featuresQuery.isError) {
    return (
      <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
        {describe(featuresQuery.error)}
      </div>
    );
  }
  if (!featuresQuery.data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  const features = featuresQuery.data.features;
  const providerRows = providersQuery.data?.rows ?? [];
  const providersByFeature = new Map<string, FeatureProviderRow[]>();
  for (const row of providerRows) {
    const arr = providersByFeature.get(row.feature_path);
    if (arr) arr.push(row);
    else providersByFeature.set(row.feature_path, [row]);
  }

  // Depth-1 features (e.g. "pos") become group cards; everything below
  // renders as a feature tree inside each card.
  const roots = features.filter((f) => f.depth === 1).sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-sm font-semibold">Features</h2>
        <p className="text-xs text-muted-foreground">
          Toggle which features this scope can use. Lower scopes inherit unless they set an
          override.
        </p>
      </header>

      <div className="space-y-3">
        {roots.map((root) => (
          <FeatureGroupCard
            key={root.path}
            scope={scope}
            scopeId={id}
            root={root}
            features={features.filter(
              (f) => f.path !== root.path && f.path.startsWith(`${root.path}.`),
            )}
            providersByFeature={providersByFeature}
            readOnly={readOnly ?? false}
          />
        ))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- //
// One card per depth-1 feature root (e.g. "pos") - header + V0060 tree //
// --------------------------------------------------------------------- //

interface FeatureGroupCardProps {
  readonly scope: FeatureScopeKind;
  readonly scopeId: string;
  readonly root: FeatureEntitlementEntry;
  readonly features: FeatureEntitlementEntry[];
  readonly providersByFeature: Map<string, FeatureProviderRow[]>;
  readonly readOnly: boolean;
}

function FeatureGroupCard({
  scope,
  scopeId,
  root,
  features,
  providersByFeature,
  readOnly,
}: FeatureGroupCardProps): JSX.Element {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(true);

  const setMutation = useMutation({
    mutationFn: (next: boolean) => apiClient.setFeatureEntitlement(scope, scopeId, root.path, next),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["feature-entitlements", scope, scopeId] });
    },
  });

  const clearMutation = useMutation({
    mutationFn: () => apiClient.clearFeatureEntitlement(scope, scopeId, root.path),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["feature-entitlements", scope, scopeId] });
    },
  });

  const error = setMutation.error ?? clearMutation.error;
  const submitting = setMutation.isPending || clearMutation.isPending;
  const tree = buildFeatureTree(features, root.path);
  const hasChildren = tree.children.length > 0;

  return (
    <section className="overflow-hidden rounded-lg border bg-card">
      <header className="flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            disabled={!hasChildren}
            className="flex items-center gap-2 text-left disabled:cursor-default"
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                expanded && "rotate-90",
                !hasChildren && "opacity-0",
              )}
            />
            {root.effective ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
            ) : (
              <XCircle className="h-4 w-4 text-rose-600" aria-hidden />
            )}
            <span className="text-sm font-medium">{root.display_name}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">
              {root.path}
            </span>
          </button>
          {root.description && (
            <p className="mt-1 text-xs text-muted-foreground">{root.description}</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {root.owned ? (
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-800">Set here</span>
            ) : (
              <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                Inherited
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Toggle
            checked={root.effective}
            disabled={readOnly || submitting}
            onChange={(next) => setMutation.mutate(next)}
            ariaLabel={`Toggle ${root.display_name}`}
          />
          {root.owned && !readOnly && (
            <button
              type="button"
              onClick={() => clearMutation.mutate()}
              disabled={submitting}
              className={cn(
                "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs",
                "text-muted-foreground hover:bg-muted disabled:opacity-50",
              )}
              title="Clear override and inherit from parent"
            >
              <RotateCcw className="h-3 w-3" /> Inherit
            </button>
          )}
        </div>

        {error && (
          <div className="basis-full rounded bg-destructive/10 px-2 py-1 text-xs text-destructive">
            {describe(error)}
          </div>
        )}
      </header>

      {expanded && hasChildren && (
        <div className={cn("divide-y bg-muted/40", !root.effective && "opacity-60")}>
          {!root.effective && (
            <p className="px-4 py-2 text-xs text-amber-800">
              {root.display_name} is currently turned off - features below are inactive until this
              group is enabled.
            </p>
          )}
          {tree.children.map((node) => (
            <FeatureNode
              key={node.path}
              node={node}
              scope={scope}
              scopeId={scopeId}
              providersByFeature={providersByFeature}
              readOnly={readOnly || !root.effective}
              depth={0}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// --------------------------------------------------------------------- //
// Feature tree - nested toggles using V0060 endpoints                  //
// --------------------------------------------------------------------- //

interface FeatureTreeNode {
  readonly path: string;
  readonly entry: FeatureEntitlementEntry | null;
  readonly children: FeatureTreeNode[];
}

function buildFeatureTree(features: FeatureEntitlementEntry[], rootPath: string): FeatureTreeNode {
  // Map every entry by path, skip the root itself (the app card already
  // shows it via V0046).
  const byPath = new Map<string, FeatureEntitlementEntry>();
  for (const f of features) {
    if (f.path !== rootPath) byPath.set(f.path, f);
  }
  const root: FeatureTreeNode = { path: rootPath, entry: null, children: [] };
  const nodesByPath = new Map<string, FeatureTreeNode>();
  nodesByPath.set(rootPath, root);

  // Walk by depth so parents always exist before their children.
  const sorted = [...byPath.values()].sort(
    (a, b) => a.depth - b.depth || a.sort_order - b.sort_order,
  );
  for (const entry of sorted) {
    const labels = entry.path.split(".");
    const parentPath = labels.slice(0, -1).join(".");
    const parent = nodesByPath.get(parentPath) ?? root;
    const node: FeatureTreeNode = { path: entry.path, entry, children: [] };
    parent.children.push(node);
    nodesByPath.set(entry.path, node);
  }
  return root;
}

interface FeatureNodeProps {
  readonly node: FeatureTreeNode;
  readonly scope: FeatureScopeKind;
  readonly scopeId: string;
  readonly providersByFeature: Map<string, FeatureProviderRow[]>;
  readonly readOnly: boolean;
  readonly depth: number;
}

function FeatureNode({
  node,
  scope,
  scopeId,
  providersByFeature,
  readOnly,
  depth,
}: FeatureNodeProps): JSX.Element {
  const queryClient = useQueryClient();
  const queryKey = ["feature-entitlements", scope, scopeId];

  const setMutation = useMutation({
    mutationFn: (next: boolean) => apiClient.setFeatureEntitlement(scope, scopeId, node.path, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const clearMutation = useMutation({
    mutationFn: () => apiClient.clearFeatureEntitlement(scope, scopeId, node.path),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const submitting = setMutation.isPending || clearMutation.isPending;
  const error = setMutation.error ?? clearMutation.error;
  const entry = node.entry;
  if (!entry) {
    // Intermediate node we don't have data for - render children only.
    return (
      <>
        {node.children.map((child) => (
          <FeatureNode
            key={child.path}
            node={child}
            scope={scope}
            scopeId={scopeId}
            providersByFeature={providersByFeature}
            readOnly={readOnly}
            depth={depth}
          />
        ))}
      </>
    );
  }

  const lockedFromAbove =
    !entry.effective && entry.disabled_by !== null && entry.disabled_by !== "scope";
  const inherited = !entry.owned;
  const indent = depth * 16;

  return (
    <>
      <div
        className="flex items-start justify-between gap-3 px-4 py-2"
        style={{ paddingLeft: 16 + indent }}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm">
            <span
              className={cn(
                "h-2 w-2 rounded-full",
                entry.effective ? "bg-emerald-500" : "bg-muted-foreground/60",
              )}
              aria-hidden
            />
            <span className="font-medium text-foreground">{entry.display_name}</span>
            {entry.is_provider_bindable ? null : (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                Group
              </span>
            )}
          </div>
          {entry.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{entry.description}</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
            {lockedFromAbove ? (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                Turned off by{" "}
                {entry.disabled_by === "feature_path" ? "a parent feature" : "a parent scope"}
              </span>
            ) : null}
            {inherited && !lockedFromAbove ? (
              <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                Inherited
              </span>
            ) : null}
            {entry.owned ? (
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-blue-800">Set here</span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Toggle
            checked={entry.effective}
            disabled={readOnly || submitting || lockedFromAbove}
            onChange={(next) => setMutation.mutate(next)}
            ariaLabel={`Toggle ${entry.display_name}`}
          />
          {entry.owned && !readOnly && (
            <button
              type="button"
              onClick={() => clearMutation.mutate()}
              disabled={submitting}
              className={cn(
                "inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-[11px]",
                "text-muted-foreground hover:bg-muted disabled:opacity-50",
              )}
              title="Clear override and inherit from parent"
            >
              <RotateCcw className="h-3 w-3" /> Inherit
            </button>
          )}
        </div>
      </div>
      {error && (
        <div
          className="px-4 pb-2 text-[11px] text-destructive"
          style={{ paddingLeft: 16 + indent }}
        >
          {describe(error)}
        </div>
      )}
      {entry.is_provider_bindable && entry.effective && !lockedFromAbove && (
        <AllowedProvidersStrip
          scope={scope}
          scopeId={scopeId}
          featurePath={entry.path}
          rows={providersByFeature.get(entry.path) ?? []}
          readOnly={readOnly}
          indentPx={16 + indent}
        />
      )}
      {node.children.map((child) => (
        <FeatureNode
          key={child.path}
          node={child}
          scope={scope}
          scopeId={scopeId}
          providersByFeature={providersByFeature}
          readOnly={readOnly}
          depth={depth + 1}
        />
      ))}
    </>
  );
}

/** Compact horizontal list of provider chips under a provider-bindable
 *  feature row. One chip per active provider - green = currently
 *  allowed at this scope, slate = currently disallowed. Clicking flips
 *  the allowlist row. Owned (explicitly-set) chips show a small dot
 *  so the admin sees at a glance what's an explicit override vs an
 *  inherited default. */
function AllowedProvidersStrip(props: {
  readonly scope: FeatureScopeKind;
  readonly scopeId: string;
  readonly featurePath: string;
  readonly rows: FeatureProviderRow[];
  readonly readOnly: boolean;
  readonly indentPx: number;
}): JSX.Element | null {
  const { scope, scopeId, featurePath, rows, readOnly, indentPx } = props;
  const queryClient = useQueryClient();
  const queryKey = ["feature-providers", scope, scopeId];

  const setMutation = useMutation({
    mutationFn: (input: { providerSlug: string; isAllowed: boolean }) =>
      apiClient.setFeatureProviderAllowlist(
        scope,
        scopeId,
        featurePath,
        input.providerSlug,
        input.isAllowed,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const clearMutation = useMutation({
    mutationFn: (providerSlug: string) =>
      apiClient.clearFeatureProviderAllowlist(scope, scopeId, featurePath, providerSlug),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  if (rows.length === 0) return null;

  const error = setMutation.error ?? clearMutation.error;
  const submitting = setMutation.isPending || clearMutation.isPending;

  return (
    <div className="px-4 pb-2 pt-0.5" style={{ paddingLeft: indentPx + 18 }}>
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="text-muted-foreground">Allowed providers:</span>
        {rows.map((row) => {
          const allowed = row.effective_allowed;
          const owned = row.owned;
          // V0063 - controlled-rollout pair with no scope-level ALLOW
          // (effective=false AND not explicitly disallowed at this scope)
          // is "Awaiting platform grant" rather than just disallowed.
          const isControlled = row.rollout_policy === "controlled";
          const isAwaiting = isControlled && !allowed && !owned;
          return (
            <button
              key={row.provider_slug}
              type="button"
              disabled={readOnly || submitting}
              onClick={() => {
                if (owned) {
                  // Toggle from explicit-set back to inheritance.
                  clearMutation.mutate(row.provider_slug);
                } else {
                  // Write a new explicit row that flips the effective
                  // state. From inherited-allowed → explicit-disallow,
                  // and from inherited-disallow → explicit-allow.
                  // For controlled rollouts where no upstream ALLOW
                  // exists, the admin-api will 403 unless the caller
                  // is super_admin - that error bubbles up via the
                  // mutation's error state below.
                  setMutation.mutate({
                    providerSlug: row.provider_slug,
                    isAllowed: !allowed,
                  });
                }
              }}
              title={
                isAwaiting
                  ? `Pilot rollout - ask platform admin to grant ${row.provider_display_name} for this feature`
                  : owned
                    ? `Clear override (revert to inheritance)`
                    : allowed
                      ? `Disallow ${row.provider_display_name} for this feature`
                      : `Allow ${row.provider_display_name} for this feature`
              }
              className={cn(
                "inline-flex items-center gap-1 rounded-md border px-2 py-0.5",
                "transition disabled:opacity-50",
                isAwaiting
                  ? "border-dashed border-amber-300 bg-amber-50/50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-300"
                  : allowed
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200"
                    : "border-border bg-muted text-muted-foreground line-through",
              )}
            >
              <Power className="h-2.5 w-2.5" aria-hidden />
              <span>{row.provider_display_name}</span>
              {isControlled && (
                <span
                  className="rounded-sm bg-amber-500/15 px-1 text-[9px] font-medium uppercase text-amber-700 dark:text-amber-300"
                  aria-label="Pilot rollout"
                >
                  Pilot
                </span>
              )}
              {owned && <span className="h-1 w-1 rounded-full bg-blue-500" aria-hidden />}
            </button>
          );
        })}
      </div>
      {error && <p className="mt-1 text-[11px] text-destructive">{describe(error)}</p>}
    </div>
  );
}
